import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Mountain, TrendingUp, AlertTriangle, Route, BarChart3, ExternalLink, Target, Zap, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn, getFlagEmoji } from '@/lib/utils';
import { computeDnfStats } from '@/lib/dnf';
import type { RaceFormat } from '@/gql/graphql';

const FORMAT_LABELS: Record<string, string> = {
    _20K: '20K',
    _50K: '50K',
    _100K: '100K',
    _100M: '100M',
};

interface FormatStat {
    format?: RaceFormat | null;
    index?: number | null;
    finishedRaces?: number | null;
}

interface RunnerStatsModalProps {
    runner: {
        name?: string | null;
        pictureUri?: string | null;
        gender?: string | null;
        nationality?: string | null;
        utmbUri?: string | null;
        utmbIndex?: number | null;
        dnfRatio?: number | null;
        longestFinishedRaceKm?: number | null;
        formatStats?: FormatStat[] | null;
    };
    raceFormat?: string | null;
    totalResults?: number;
    onClose: () => void;
    // Live mode (optional)
    predictedPosition?: number | null;
    liveScore?: number | null;
    liveRunnerUrl?: string | null;
    livePosition?: number | null;     // current live ranking (0 = DNF, null = not ranked)
    liveIsDnf?: boolean;
    liveIsDns?: boolean;
}

export function RunnerStatsModal({ runner, raceFormat, onClose, predictedPosition, liveScore, liveRunnerUrl, livePosition, liveIsDnf, liveIsDns }: RunnerStatsModalProps) {
    const { t } = useTranslation('races');

    // Lock body scroll while modal is open
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    const formatLabel = raceFormat ? FORMAT_LABELS[raceFormat] ?? raceFormat : null;

    const currentFormatStats = runner.formatStats?.find(
        (s) => s.format === raceFormat,
    );

    const dnfStats = computeDnfStats(runner.dnfRatio, runner.formatStats);

    return createPortal(
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
            <div
                className="absolute inset-0 bg-slate-900/20 backdrop-blur-[2px] animate-in fade-in duration-200"
                onClick={onClose}
            />

            <div role="dialog" aria-modal="true" className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-sm relative z-10 animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200 border border-slate-100">
                {/* Header */}
                <div className="flex items-center gap-3 p-5 pb-4 border-b border-slate-100">
                    <div className="relative flex-shrink-0">
                        <div className="w-12 h-12 rounded-xl overflow-hidden bg-slate-100 border border-slate-100">
                            {runner.pictureUri ? (
                                <img src={runner.pictureUri} className="w-full h-full object-cover" alt={runner.name || ''} />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                    <Mountain className="text-slate-300" size={20} />
                                </div>
                            )}
                        </div>
                        {runner.gender && (
                            <span className={cn(
                                "absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 border-2 border-white rounded-full",
                                runner.gender === 'MALE' ? "bg-blue-500" : "bg-pink-500"
                            )} />
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="font-bold text-slate-900 truncate">{runner.name}</p>
                        <p className="text-xs text-slate-400">{t('details.runnerStats.title')}</p>
                    </div>
                    <span className="text-lg leading-none" title={runner.nationality || ''}>
                        {getFlagEmoji(runner.nationality)}
                    </span>
                    <button
                        onClick={onClose}
                        className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Live section */}
                {(livePosition != null || liveIsDnf || liveIsDns || predictedPosition != null) && (
                    <div className="px-5 pt-4 pb-3 border-b border-slate-100 space-y-3">
                        {/* Current live position */}
                        {(livePosition != null || liveIsDnf || liveIsDns) && (
                            <StatRow
                                icon={<MapPin size={16} />}
                                iconClass={liveIsDns ? "text-slate-400 bg-slate-50" : liveIsDnf ? "text-red-500 bg-red-50" : "text-rose-600 bg-rose-50"}
                                label={t('details.runnerStats.currentPosition')}
                                value={liveIsDns ? 'DNS' : liveIsDnf ? 'DNF' : `#${livePosition}`}
                            />
                        )}

                        {/* User's bet on this runner */}
                        {predictedPosition != null && (
                            <>
                                <p className="text-xs font-bold text-emerald-700 uppercase tracking-wide mt-1">{t('details.runnerStats.yourBet')}</p>
                                <StatRow
                                    icon={<Target size={16} />}
                                    iconClass="text-emerald-600 bg-emerald-50"
                                    label={t('details.runnerStats.predictedPosition')}
                                    value={predictedPosition === 0 ? 'DNF' : `#${predictedPosition}`}
                                />
                                {liveScore != null && (
                                    <StatRow
                                        icon={<Zap size={16} />}
                                        iconClass={liveScore >= 0 ? "text-emerald-600 bg-emerald-50" : "text-red-500 bg-red-50"}
                                        label={t('details.runnerStats.estimatedScore')}
                                        value={`${liveScore > 0 ? '+' : ''}${liveScore.toFixed(1)} pts`}
                                    />
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* Stats */}
                <div className="p-5 space-y-3">
                    {/* General UTMB Index */}
                    <StatRow
                        icon={<TrendingUp size={16} />}
                        iconClass="text-emerald-600 bg-emerald-50"
                        label={t('details.runnerStats.generalIndex')}
                        value={runner.utmbIndex != null ? Math.round(runner.utmbIndex).toString() : '—'}
                    />

                    {/* Format-specific index */}
                    {formatLabel && (
                        <StatRow
                            icon={<BarChart3 size={16} />}
                            iconClass="text-indigo-600 bg-indigo-50"
                            label={t('details.runnerStats.formatIndex', { format: formatLabel })}
                            value={currentFormatStats?.index != null ? Math.round(currentFormatStats.index).toString() : '—'}
                        />
                    )}

                    {/* Finished races in format */}
                    {formatLabel && (
                        <StatRow
                            icon={<Mountain size={16} />}
                            iconClass="text-amber-600 bg-amber-50"
                            label={t('details.runnerStats.finishedRaces', { format: formatLabel })}
                            value={currentFormatStats?.finishedRaces != null
                                ? t('details.runnerStats.races', { count: currentFormatStats.finishedRaces })
                                : '—'
                            }
                        />
                    )}

                    {/* DNF Ratio as fraction */}
                    <StatRow
                        icon={<AlertTriangle size={16} />}
                        iconClass="text-red-500 bg-red-50"
                        label={t('details.runnerStats.dnfRatio')}
                        value={dnfStats != null
                            ? `${dnfStats.dnfCount} / ${dnfStats.totalRaces}`
                            : '—'
                        }
                    />

                    {/* Longest finished race */}
                    <StatRow
                        icon={<Route size={16} />}
                        iconClass="text-sky-600 bg-sky-50"
                        label={t('details.runnerStats.longestRace')}
                        value={runner.longestFinishedRaceKm != null
                            ? `${Math.round(runner.longestFinishedRaceKm)} km`
                            : '—'
                        }
                    />
                </div>

                {/* UTMB link */}
                {(liveRunnerUrl || runner.utmbUri) && (
                    <div className="px-5 pb-5">
                        <a
                            href={liveRunnerUrl ?? `https://utmb.world/en/runner/${runner.utmbUri}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 w-full py-2.5 text-sm font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-colors"
                        >
                            {liveRunnerUrl ? t('details.runnerStats.utmbLive') : t('details.runnerStats.utmbProfile')}
                            <ExternalLink size={14} />
                        </a>
                    </div>
                )}
            </div>
        </div>,
        document.body,
    );
}

function StatRow({ icon, iconClass, label, value }: {
    icon: React.ReactNode;
    iconClass: string;
    label: string;
    value: string;
}) {
    return (
        <div className="flex items-center gap-3">
            <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0", iconClass)}>
                {icon}
            </div>
            <span className="text-sm text-slate-600 flex-1">{label}</span>
            <span className="text-sm font-bold text-slate-900">{value}</span>
        </div>
    );
}
